import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import SubscribeForm from './SubscribeForm';
import { WORKER_URL } from '../../utils/api';

const DOR_LABEL = {
  'pedidos-atrasados':    { label: 'Alertas',     color: '#d97706', bg: '#fffbeb' },
  'dashboard-tempo-real': { label: 'Dashboard',   color: '#0369a1', bg: '#f0f9ff' },
  'flex-cores':           { label: 'FLEX',         color: '#16a34a', bg: '#f0fdf4' },
  'documentos-fiscais':   { label: 'Documentos',  color: '#7c3aed', bg: '#f5f3ff' },
  'perguntas-clientes':   { label: 'Perguntas',   color: '#db2777', bg: '#fdf2f8' },
  'fechar-mes':           { label: 'Relatórios',  color: '#1B2A7A', bg: '#EEF2FF' },
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateShort(iso) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ArticleSchema({ post }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.meta_description || post.summary,
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: { '@type': 'Organization', name: 'Integra MKP', url: 'https://integramkp.com.br' },
    publisher: { '@type': 'Organization', name: 'Integra MKP', url: 'https://integramkp.com.br' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://integramkp.com.br/blog/${post.slug}` },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function RelatedCard({ post }) {
  const dor = DOR_LABEL[post.dor_tema];
  return (
    <a href={`/blog/${post.slug}`} style={{ display: 'block', textDecoration: 'none', padding: '14px 16px', borderRadius: 10, border: '1px solid #E4E0D4', background: '#fff', marginBottom: 10, transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      {dor && (
        <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: dor.color, background: dor.bg, padding: '2px 8px', borderRadius: 20, marginBottom: 6 }}>
          {dor.label}
        </span>
      )}
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#0E1430', lineHeight: 1.35, fontFamily: "'Archivo', sans-serif" }}>
        {post.title}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9CA3AF' }}>{formatDateShort(post.published_at)}</p>
    </a>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`${WORKER_URL}/api/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(post => {
        setPost(post);
        fetch(`${WORKER_URL}/api/blog/${slug}/view`, { method: 'POST' }).catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/blog?page=1`)
      .then(r => r.json())
      .then(d => setRelated((d.posts || []).filter(p => p.slug !== slug).slice(0, 5)))
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <BlogLayout title="Carregando...">
        <div style={{ textAlign: 'center', padding: '100px 24px', color: '#9CA3AF' }}>Carregando artigo...</div>
      </BlogLayout>
    );
  }

  if (notFound || !post) {
    return (
      <BlogLayout title="Artigo não encontrado">
        <div style={{ textAlign: 'center', padding: '100px 24px' }}>
          <h1 style={{ fontFamily: "'Archivo', sans-serif", fontSize: 32, color: '#0E1430', marginBottom: 12 }}>Artigo não encontrado</h1>
          <a href="/blog" style={{ color: '#1B2A7A', fontWeight: 600 }}>← Voltar ao blog</a>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout title={post.meta_title || post.title} description={post.meta_description || post.summary}>
      <ArticleSchema post={post} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px 80px', display: 'flex', gap: 48, alignItems: 'flex-start' }}>

        {/* ── Artigo principal ── */}
        <article style={{ flex: 1, minWidth: 0 }}>
          <a href="/blog" style={{ fontSize: 13, color: '#6A7091', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 32 }}>
            ← Blog
          </a>

          <header style={{ marginBottom: 40 }}>
            <h1 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 'clamp(28px, 5vw, 44px)', lineHeight: 1.1, letterSpacing: '-0.03em', color: '#0E1430', margin: '0 0 16px' }}>
              {post.title}
            </h1>
            {post.summary && (
              <p style={{ fontSize: 18, color: '#6A7091', lineHeight: 1.6, margin: '0 0 20px' }}>{post.summary}</p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, paddingTop: 16, borderTop: '2px solid #FFD400' }}>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>
                <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
              </span>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>·</span>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>Integra MKP</span>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>·</span>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>
                {Math.max(1, Math.round((post.content || '').replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length / 200))} min de leitura
              </span>
            </div>
          </header>

          <div
            className="blog-content"
            style={{ fontSize: 17, lineHeight: 1.75, color: '#3B4266' }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Compartilhar */}
          {(() => {
            const shareUrl = `https://integramkp.com.br/blog/${slug}`;
            const shareText = encodeURIComponent(post.title + ' — ' + shareUrl);
            const copy = () => { navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
            return (
              <div style={{ marginTop: 40, paddingTop: 28, borderTop: '1px solid #E4E0D4', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6A7091', marginRight: 4 }}>Compartilhar:</span>
                <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: '#25D366', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  WhatsApp
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: '#1877F2', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  Facebook
                </a>
                <button onClick={copy}
                  style={{ padding: '7px 16px', background: copied ? '#16a34a' : '#F0EDE6', color: copied ? '#fff' : '#3B4266', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {copied ? '✓ Copiado!' : 'Copiar link'}
                </button>
              </div>
            );
          })()}

          {/* FAQ */}
          {(() => { try { const faq = JSON.parse(post.faq || '[]'); if (!faq.length) return null;
            return (
              <section style={{ marginTop: 48 }}>
                <h2 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 22, color: '#0E1430', marginBottom: 16 }}>Perguntas frequentes</h2>
                {faq.map((item, i) => (
                  <details key={i} style={{ border: '1px solid #E4E0D4', borderRadius: 10, marginBottom: 8, padding: '14px 18px' }}>
                    <summary style={{ fontWeight: 700, fontSize: 15, color: '#0E1430', cursor: 'pointer', fontFamily: "'Archivo', sans-serif" }}>{item.q}</summary>
                    <p style={{ margin: '10px 0 0', fontSize: 15, color: '#3B4266', lineHeight: 1.65 }}>{item.a}</p>
                  </details>
                ))}
              </section>
            );
          } catch { return null; } })()}

          {/* Bloco CTA + Dashboard + Newsletter */}
          <div style={{ marginTop: 56, borderRadius: 20, overflow: 'hidden', border: '1px solid #E4E0D4' }}>

            {/* CTA topo */}
            <div style={{ background: '#0A0F33', padding: '28px 32px', textAlign: 'center' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#FFD400', letterSpacing: '0.1em', marginBottom: 8, textTransform: 'uppercase' }}>Integra MKP</p>
              <h3 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 22, color: '#FAF8F3', letterSpacing: '-0.02em', margin: '0 0 8px' }}>
                Resolva isso hoje mesmo.
              </h3>
              <p style={{ color: 'rgba(250,248,243,0.7)', fontSize: 14, marginBottom: 18 }}>
                Dashboard completo para vendedores do Mercado Livre. 7 dias grátis. Sem cartão.
              </p>
              <a href="/" style={{ display: 'inline-block', background: '#FFD400', color: '#0A0F33', padding: '11px 28px', borderRadius: 100, fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 15, textDecoration: 'none' }}>
                Testar grátis →
              </a>
            </div>

            {/* Screenshot do dashboard */}
            <div style={{ background: '#F0EDE6', padding: '20px 24px', textAlign: 'center' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Como funciona por dentro</p>
              <img
                src="/screenshots/dashboard.png"
                alt="Dashboard Integra MKP — painel ao vivo para vendedores do Mercado Livre"
                style={{ width: '100%', maxWidth: 640, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #E4E0D4' }}
              />
            </div>

            {/* Newsletter */}
            <div style={{ background: '#1B2A7A', padding: '28px 32px', textAlign: 'center' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#FFD400', letterSpacing: '0.1em', marginBottom: 8, textTransform: 'uppercase' }}>Newsletter gratuita</p>
              <h3 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 20, color: '#FAF8F3', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
                Dicas semanais para vender mais
              </h3>
              <p style={{ color: 'rgba(250,248,243,0.65)', fontSize: 13, margin: '0 0 18px' }}>Sem spam. Cancele quando quiser.</p>
              <SubscribeForm source="article-inline" compact />
            </div>

          </div>

          <div style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid #E4E0D4', textAlign: 'center' }}>
            <a href="/blog" style={{ fontSize: 15, color: '#1B2A7A', fontWeight: 600, textDecoration: 'none' }}>← Ver mais artigos</a>
          </div>
        </article>

        {/* ── Sidebar de artigos recentes ── */}
        {related.length > 0 && (
          <aside className="blog-sidebar" style={{ width: 280, flexShrink: 0 }}>
            <div style={{ position: 'sticky', top: 80 }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#1B2A7A', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 14 }}>
                Leia também
              </p>
              {related.map(p => <RelatedCard key={p.slug} post={p} />)}
              <a href="/blog" style={{ display: 'block', textAlign: 'center', marginTop: 14, fontSize: 13, color: '#1B2A7A', fontWeight: 600, textDecoration: 'none' }}>
                Ver todos os artigos →
              </a>
            </div>
          </aside>
        )}
      </div>


      <style>{`
        .blog-content h2 { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 26px; color: #0E1430; margin: 40px 0 16px; line-height: 1.2; letter-spacing: -0.02em; }
        .blog-content h3 { font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 20px; color: #1B2A7A; margin: 32px 0 12px; }
        .blog-content p { margin: 0 0 20px; }
        .blog-content ul, .blog-content ol { padding-left: 24px; margin: 0 0 20px; }
        .blog-content li { margin-bottom: 8px; }
        .blog-content strong { color: #0E1430; font-weight: 700; }
        .blog-content a { color: #1B2A7A; }
        @media (max-width: 768px) {
          .blog-sidebar { display: none !important; }
        }
      `}</style>
    </BlogLayout>
  );
}
